/**
 * EatOkra Article Tracking — Roux article pages only.
 *
 * Fires read-depth milestones (25/50/75/complete) and internal vs
 * outbound link clicks inside the article body. The article_view event
 * is pushed inline from the HAML before this pack runs.
 *
 * The general [data-track-click] delegated handler (used by typed
 * events outside the article body, e.g. related-post cards) lives in
 * eatokra_tracking_core.js, loaded globally via application.html.haml.
 *
 * Pattern matches eatokra_homepage_tracking.js: dataLayer.push is
 * unconditional; GA4 Consent Mode v2 is enforced downstream by GTM.
 */
(function () {
  'use strict';

  var READ_MILESTONES = [25, 50, 75, 100];
  var COMPLETE_MIN_SECONDS = 30;
  var fired = {};
  var pageStartedAt = Date.now();

  function pushEvent(eventName, props) {
    var payload = Object.assign(
      {
        event: eventName,
        page_path: location.pathname,
        page_url: location.href,
        timestamp: Date.now()
      },
      props || {}
    );
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push(payload);
    }
  }

  function articleBody() {
    return document.querySelector('[data-article-body="true"]');
  }

  function articleId(bodyEl) {
    return (bodyEl && bodyEl.getAttribute('data-article-id')) || '';
  }

  function articleSlug(bodyEl) {
    return (bodyEl && bodyEl.getAttribute('data-article-slug')) || '';
  }

  // --- Read-depth ---

  function initReadDepth() {
    var body = articleBody();
    if (!body) return;
    var id = articleId(body);
    var slug = articleSlug(body);

    function check() {
      var rect = body.getBoundingClientRect();
      var bodyTopPage = rect.top + window.pageYOffset;
      var bodyHeight = rect.height;
      if (bodyHeight <= 0) return;
      var scrolled = (window.pageYOffset + window.innerHeight) - bodyTopPage;
      var pct = Math.max(0, Math.min(100, (scrolled / bodyHeight) * 100));

      READ_MILESTONES.forEach(function (m) {
        if (pct < m) return;

        // Single article_scroll_depth event with depth property — fires once per threshold.
        var depthKey = 'depth_' + m;
        if (!fired[depthKey]) {
          fired[depthKey] = true;
          pushEvent('article_scroll_depth', {
            article_id: id,
            article_slug: slug,
            scroll_depth: m
          });
        }

        // article_read_complete: 100% + 30s dwell gate. Fires once.
        if (m === 100) {
          var elapsed = (Date.now() - pageStartedAt) / 1000;
          if (elapsed < COMPLETE_MIN_SECONDS) return;
          if (fired.complete) return;
          fired.complete = true;
          pushEvent('article_read_complete', {
            article_id: id,
            article_slug: slug,
            time_on_page_sec: Math.round(elapsed)
          });
        }
      });
    }

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          check();
          ticking = false;
        });
      },
      { passive: true }
    );

    setTimeout(check, COMPLETE_MIN_SECONDS * 1000);
    check();
  }

  // --- Internal vs outbound link clicks inside article body ---

  function initLinkTracking() {
    var body = articleBody();
    if (!body) return;
    var id = articleId(body);
    var slug = articleSlug(body);

    body.addEventListener(
      'click',
      function (event) {
        var el = event.target;
        while (el && el !== body && el.tagName !== 'A') {
          el = el.parentNode;
        }
        if (!el || el.tagName !== 'A') return;
        var href = el.getAttribute('href') || '';
        if (!href || href.charAt(0) === '#') return;
        if (/^(mailto|tel|javascript):/i.test(href)) return;

        var destUrl;
        try {
          destUrl = new URL(href, location.origin);
        } catch (e) {
          return;
        }
        var isInternal = destUrl.hostname === location.hostname;
        var linkText = (el.textContent || '').trim().substring(0, 120);

        if (isInternal) {
          var path = destUrl.pathname;
          var destType = 'other';
          if (/^\/businesses\//.test(path)) destType = 'restaurant';
          else if (/^\/posts\/|^\/blog\//.test(path)) destType = 'article';
          else if (/^\/collections\//.test(path)) destType = 'collection';
          else if (/^\/events\//.test(path)) destType = 'event';

          pushEvent('article_internal_link_click', {
            article_id: id,
            article_slug: slug,
            destination_path: path,
            destination_type: destType,
            link_text: linkText
          });
        } else {
          pushEvent('article_outbound_link_click', {
            article_id: id,
            article_slug: slug,
            destination_url: destUrl.href,
            destination_domain: destUrl.hostname,
            link_text: linkText
          });
        }
      },
      true
    );
  }

  function init() {
    initReadDepth();
    initLinkTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
